/**
 * Copyright (c) 2023 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Gianluca Tomasello <giagitom@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { ParamDefinition as PD } from '../../../mol-util/param-definition';
import { Vec3 } from '../../../mol-math/linear-algebra';
import { Unit } from '../../../mol-model/structure';
import { Mesh } from '../../../mol-geo/geometry/mesh/mesh';
import { MeshBuilder } from '../../../mol-geo/geometry/mesh/mesh-builder';
import { Segmentation } from '../../../mol-data/int';
import { isNucleic } from '../../../mol-model/structure/model/types';
import { UnitsMeshParams, UnitsMeshVisual } from '../units-visual';
import { NucleotideLocationIterator, getNucleotideElementLoci, eachNucleotideElement, getNucleotideBaseType, createNucleicIndices, setSugarIndices, hasSugarIndices, setPurinIndices, hasPyrimidineIndices, setPyrimidineIndices, hasPurinIndices } from './util/nucleotide';
import { Sphere3D } from '../../../mol-math/geometry';
// TODO support ring-fills for multiple locations (including from microheterogeneity)
var pN1 = Vec3();
var pC2 = Vec3();
var pN3 = Vec3();
var pC4 = Vec3();
var pC5 = Vec3();
var pC6 = Vec3();
var pN7 = Vec3();
var pC8 = Vec3();
var pN9 = Vec3();
var pC1_1 = Vec3();
var pC2_1 = Vec3();
var pC3_1 = Vec3();
var pC4_1 = Vec3();
var pO4_1 = Vec3();
var mid = Vec3();
var normal = Vec3();
var shift = Vec3();
export var NucleotideAtomicRingFillMeshParams = {
    sizeFactor: PD.Numeric(0.2, { min: 0, max: 10, step: 0.01 }),
    thicknessFactor: PD.Numeric(1, { min: 0, max: 2, step: 0.01 }),
};
export var DefaultNucleotideAtomicRingFillMeshProps = PD.getDefaultValues(NucleotideAtomicRingFillMeshParams);
var positionsRing5_6 = new Float32Array(2 * 9 * 3);
var stripIndicesRing5_6 = new Uint32Array([0, 1, 2, 3, 4, 5, 6, 7, 16, 17, 14, 15, 12, 13, 8, 9, 10, 11, 0, 1]);
var fanIndicesTopRing5_6 = new Uint32Array([8, 12, 14, 16, 6, 4, 2, 0, 10]);
var fanIndicesBottomRing5_6 = new Uint32Array([9, 11, 1, 3, 5, 7, 17, 15, 13]);
var positionsRing5 = new Float32Array(2 * 6 * 3);
var stripIndicesRing5 = new Uint32Array([2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 2, 3]);
var fanIndicesTopRing5 = new Uint32Array([0, 10, 8, 6, 4, 2, 10]);
var fanIndicesBottomRing5 = new Uint32Array([1, 3, 5, 7, 9, 11, 3]);
var positionsRing6 = new Float32Array(2 * 6 * 3);
var stripIndicesRing6 = new Uint32Array([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1]);
var fanIndicesTopRing6 = new Uint32Array([0, 10, 8, 6, 4, 2]);
var fanIndicesBottomRing6 = new Uint32Array([1, 3, 5, 7, 9, 11]);
var tmpShiftV = Vec3();
function shiftPositions(out, dir) {
    var positions = [];
    for (var _i = 2; _i < arguments.length; _i++) {
        positions[_i - 2] = arguments[_i];
    }
    for (var i = 0, il = positions.length; i < il; ++i) {
        var v = positions[i];
        Vec3.toArray(Vec3.add(tmpShiftV, v, dir), out, (i * 2) * 3);
        Vec3.toArray(Vec3.sub(tmpShiftV, v, dir), out, (i * 2 + 1) * 3);
    }
}
function createNucleotideAtomicRingFillMesh(ctx, unit, structure, theme, props, mesh) {
    if (!Unit.isAtomic(unit))
        return Mesh.createEmpty(mesh);
    var nucleotideElementCount = unit.nucleotideElements.length;
    if (!nucleotideElementCount)
        return Mesh.createEmpty(mesh);
    var sizeFactor = props.sizeFactor, thicknessFactor = props.thicknessFactor;
    var vertexCount = nucleotideElementCount * 25;
    var builderState = MeshBuilder.createState(vertexCount, vertexCount / 4, mesh);
    var elements = unit.elements, model = unit.model;
    var _a = model.atomicHierarchy, chainAtomSegments = _a.chainAtomSegments, residueAtomSegments = _a.residueAtomSegments;
    var moleculeType = model.atomicHierarchy.derived.residue.moleculeType;
    var pos = unit.conformation.invariantPosition;
    var chainIt = Segmentation.transientSegments(chainAtomSegments, elements);
    var residueIt = Segmentation.transientSegments(residueAtomSegments, elements);
    var thickness = sizeFactor * thicknessFactor;
    var i = 0;
    while (chainIt.hasNext) {
        residueIt.setSegment(chainIt.move());
        while (residueIt.hasNext) {
            var residueIndex = residueIt.move().index;
            if (isNucleic(moleculeType[residueIndex])) {
                var idx = createNucleicIndices();
                builderState.currentGroup = i;
                setSugarIndices(idx, unit, residueIndex);
                if (hasSugarIndices(idx)) {
                    pos(idx.C1_1, pC1_1);
                    pos(idx.C2_1, pC2_1);
                    pos(idx.C3_1, pC3_1);
                    pos(idx.C4_1, pC4_1);
                    pos(idx.O4_1, pO4_1);
                    // sugar ring
                    Vec3.triangleNormal(normal, pC3_1, pC4_1, pC1_1);
                    Vec3.scale(mid, Vec3.add(mid, pO4_1, Vec3.add(mid, pC4_1, Vec3.add(mid, pC3_1, Vec3.add(mid, pC1_1, pC2_1)))), 0.2 /* 1 / 5 */);
                    Vec3.scale(shift, normal, thickness);
                    shiftPositions(positionsRing5, shift, mid, pC3_1, pC4_1, pO4_1, pC1_1, pC2_1);
                    MeshBuilder.addTriangleStrip(builderState, positionsRing5, stripIndicesRing5);
                    MeshBuilder.addTriangleFanWithNormal(builderState, positionsRing5, fanIndicesTopRing5, normal);
                    Vec3.negate(normal, normal);
                    MeshBuilder.addTriangleFanWithNormal(builderState, positionsRing5, fanIndicesBottomRing5, normal);
                }
                var _b = getNucleotideBaseType(unit, residueIndex), isPurine = _b.isPurine, isPyrimidine = _b.isPyrimidine;
                if (isPurine) {
                    setPurinIndices(idx, unit, residueIndex);
                    if (hasPurinIndices(idx)) {
                        pos(idx.N1, pN1);
                        pos(idx.C2, pC2);
                        pos(idx.N3, pN3);
                        pos(idx.C4, pC4);
                        pos(idx.C5, pC5);
                        pos(idx.C6, pC6);
                        pos(idx.N7, pN7);
                        pos(idx.C8, pC8), pos(idx.N9, pN9);
                        // base ring
                        Vec3.triangleNormal(normal, pN1, pC4, pC5);
                        Vec3.scale(shift, normal, thickness);
                        shiftPositions(positionsRing5_6, shift, pN1, pC2, pN3, pC4, pC5, pC6, pN7, pC8, pN9);
                        MeshBuilder.addTriangleStrip(builderState, positionsRing5_6, stripIndicesRing5_6);
                        MeshBuilder.addTriangleFanWithNormal(builderState, positionsRing5_6, fanIndicesTopRing5_6, normal);
                        Vec3.negate(normal, normal);
                        MeshBuilder.addTriangleFanWithNormal(builderState, positionsRing5_6, fanIndicesBottomRing5_6, normal);
                    }
                }
                else if (isPyrimidine) {
                    setPyrimidineIndices(idx, unit, residueIndex);
                    if (hasPyrimidineIndices(idx)) {
                        pos(idx.N1, pN1);
                        pos(idx.C2, pC2);
                        pos(idx.N3, pN3);
                        pos(idx.C4, pC4);
                        pos(idx.C5, pC5);
                        pos(idx.C6, pC6);
                        // base ring
                        Vec3.triangleNormal(normal, pN1, pC4, pC5);
                        Vec3.scale(shift, normal, thickness);
                        shiftPositions(positionsRing6, shift, pN1, pC2, pN3, pC4, pC5, pC6);
                        MeshBuilder.addTriangleStrip(builderState, positionsRing6, stripIndicesRing6);
                        MeshBuilder.addTriangleFanWithNormal(builderState, positionsRing6, fanIndicesTopRing6, normal);
                        Vec3.negate(normal, normal);
                        MeshBuilder.addTriangleFanWithNormal(builderState, positionsRing6, fanIndicesBottomRing6, normal);
                    }
                }
                ++i;
            }
        }
    }
    var m = MeshBuilder.getMesh(builderState);
    var sphere = Sphere3D.expand(Sphere3D(), unit.boundary.sphere, thickness);
    m.setBoundingSphere(sphere);
    return m;
}
export var NucleotideAtomicRingFillParams = __assign(__assign({}, UnitsMeshParams), NucleotideAtomicRingFillMeshParams);
export function NucleotideAtomicRingFillVisual(materialId) {
    return UnitsMeshVisual({
        defaultProps: PD.getDefaultValues(NucleotideAtomicRingFillParams),
        createGeometry: createNucleotideAtomicRingFillMesh,
        createLocationIterator: NucleotideLocationIterator.fromGroup,
        getLoci: getNucleotideElementLoci,
        eachLocation: eachNucleotideElement,
        setUpdateState: function (state, newProps, currentProps) {
            state.createGeometry = (newProps.sizeFactor !== currentProps.sizeFactor ||
                newProps.thicknessFactor !== currentProps.thicknessFactor);
        }
    }, materialId);
}
